// stores/collectorPrewarmStore.js
//
// The morning download's state (collector-app P2-C part B): runs the pre-warm
// of collector/prewarm.js for a day and says how far it is, for the one quiet
// line under the day header («بننزّل صور يومك 42 من 180» → «يومك جاهز من غير نت ✓»).
//
// A Pinia store so the download outlives the page that started it (F7 removes
// the previous page on every push) and the line reads the same state wherever
// the collector is.
//
//   phase  'idle'     nothing to say (no worker to cache for, nothing asked yet,
//                     or the run ended short — the ✓ is simply not shown)
//          'running'  `done` of `total` pictures are on the phone
//          'ready'    every picture the day needs is on the phone
//
// Triggers: every day the day store accepts (a 304 too — then it only checks
// the cache), and the signal coming back. Resuming an interrupted run is just
// running again: what is cached is skipped. NEVER blocks a screen — nothing
// awaits it, and a failure is a console line, not a toast.
//
// Honest by construction: it runs only when a service worker controls the page
// (without one nothing would be kept, and «جاهز من غير نت» would be a lie), and
// «جاهز» is shown only when every picture is IN THE CACHE (checked after the
// fetch, so an older worker without the photo-host route cannot fake it).
//
// After the pictures, the same run saves a copy of the supplier preview of
// every shop of the day, and refreshes the shops picked on the phone
// (supplier-preview-offline P2-A part B, locked answer L-H): one shop at a
// time, the day's shops in route order, only when the copy is missing or older
// than 2 hours, never while an offline preview is on screen and never with no
// signal. `previewReadyAt` tells the shop row when its copy was made
// («عرض جاهز 08:12», collector/dayView.js). Then ONE photo pass for the copies
// (supplier-preview-offline L-N): up to 600 pictures / 100 MB on any connection.
import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import { collectorNetwork } from '@/collector/network.js';
import { COPY_MAX_AGE_MS, isReplayOn, recordShop } from '@/collector/previewReplay.js';
import { isUsableCopy, previewStore } from '@/collector/previewStore.js';
import {
  fetchPhoto,
  PREVIEW_PHOTO_MAX_BYTES,
  runPrewarm,
  selectPrewarmUrls,
  selectPreviewPhotoUrls,
} from '@/collector/prewarm.js';
import { useAuthStore } from '@/stores/authStore.js';

/** After a run that ended with pictures missing, leave the host alone this long (the signal's return skips it). */
export const FAILED_RUN_PAUSE_MS = 5 * 60_000;
/** A copy younger than this is not made again (L-H). */
export const PREVIEW_REFRESH_MS = 2 * 60 * 60 * 1000;
/** A picked shop stays on the phone this long after it was picked (L-H). */
export const PICKED_KEEP_MS = 7 * 24 * 60 * 60 * 1000;
/** At most this many picked shops; the oldest pick drops (L-H). */
export const PICKED_MAX = 10;

/**
 * What a pass does, from what the phone holds (pure): the shops to record, in
 * order — the day's shops with a `supplierId` in route order, then the picked
 * shops — skipping a copy younger than 2 hours; and the rows to delete — a day
 * shop off the day 24 h after its recording, a picked shop 7 days after it was
 * picked, the picks beyond the 10 newest.
 *
 * @param {object|null} day - the day document (its `shops[]` in route order), or null (no day)
 * @param {Array<{shopId, source, recordedAt, pickedAt?}>} copies - what the phone holds
 * @returns {{ record: Array<{shopId: string, name: string|null, source: 'day'|'picked'}>, remove: string[] }}
 */
export function planPreviewPass(day, copies, now = Date.now()) {
  const age = (iso) => now - (Date.parse(iso ?? '') || 0);
  const byId = new Map(copies.map((row) => [String(row.shopId), row]));
  const onDay = new Map();
  for (const shop of day?.shops ?? []) {
    if (shop?.supplierId == null || onDay.has(String(shop.supplierId))) continue;
    onDay.set(String(shop.supplierId), shop?.name ?? null);
  }

  const picked = copies
    .filter((row) => row.source === 'picked')
    .sort((a, b) => (Date.parse(b.pickedAt ?? '') || 0) - (Date.parse(a.pickedAt ?? '') || 0));
  const remove = new Set(
    picked.filter((row, index) => index >= PICKED_MAX || age(row.pickedAt) >= PICKED_KEEP_MS).map((row) => String(row.shopId)),
  );
  for (const row of copies) {
    const id = String(row.shopId);
    if (row.source !== 'picked' && !onDay.has(id) && age(row.recordedAt) >= COPY_MAX_AGE_MS) remove.add(id);
  }

  const stale = (id) => !byId.has(id) || age(byId.get(id).recordedAt) >= PREVIEW_REFRESH_MS;
  const record = [];
  for (const [shopId, name] of onDay) {
    if (stale(shopId)) record.push({ shopId, name, source: byId.get(shopId)?.source === 'picked' ? 'picked' : 'day' });
  }
  for (const row of picked) {
    const id = String(row.shopId);
    if (!onDay.has(id) && !remove.has(id) && stale(id)) record.push({ shopId: id, name: row.name ?? null, source: 'picked' });
  }
  return { record, remove: [...remove] };
}

/** First visit: how long to wait for a freshly activated worker to take the page over. */
const CONTROLLER_WAIT_MS = 4_000;

/**
 * When the shop's usable copy was recorded (ISO), else null — «عرض جاهز HH:MM».
 * A plain function over the store's `copies` (not a store action: a page reads
 * it while it renders).
 */
export function previewReadyAt(copies, shopId) {
  const row = shopId == null ? null : copies?.[String(shopId)];
  const recordedAt = Date.parse(row?.recordedAt ?? '');
  return Number.isFinite(recordedAt) && Date.now() - recordedAt < COPY_MAX_AGE_MS ? row.recordedAt : null;
}

function indexEntry(row) {
  const { shopId, name, source, recordedAt, pickedAt } = row;
  return { shopId: String(shopId), name: name ?? '', source, recordedAt, ...(pickedAt ? { pickedAt } : {}) };
}

/** A worker is (or is about to be) in charge of this page's requests. */
export async function serviceWorkerControlsPage({ waitMs = CONTROLLER_WAIT_MS } = {}) {
  const container = typeof navigator === 'undefined' ? null : navigator.serviceWorker;
  if (!container || typeof container.getRegistration !== 'function') return false;
  if (container.controller) return true;
  let registration;
  try {
    registration = await container.getRegistration();
  } catch {
    return false;
  }
  if (!registration) return false; // dev server, the Mini App: no worker will ever come
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer);
      container.removeEventListener('controllerchange', done);
      resolve(Boolean(container.controller));
    };
    const timer = setTimeout(done, waitMs);
    container.addEventListener('controllerchange', done);
  });
}

// `ignoreVary`: the host answers `Vary: Origin, …`, and a request the page
// builds by hand must find what the worker stored for the <img> that asked first.
const inCache = async (url) => Boolean(await caches.match(url, { ignoreVary: true }));

export const useCollectorPrewarmStore = defineStore('collectorPrewarm', () => {
  const phase = ref('idle');
  const done = ref(0);
  const total = ref(0);

  let running = false;
  /** The newest day asked for while a run was going. */
  let pendingDay = null;
  let lastDay = null;
  let readyFor = null;
  let pausedUntil = 0;

  /** shopId → `{ shopId, name, source, recordedAt, pickedAt? }` of each copy on the phone (no responses). */
  const copies = ref({});
  let copiesLoaded = null;
  let previewRunning = false;
  let previewPending;
  /** A photo pass is owed (a page load, a new copy, or the last pass lost the signal). */
  let previewPhotosDue = true;

  function loadCopies() {
    if (!copiesLoaded) {
      copiesLoaded = previewStore()
        .list()
        .then((rows) => {
          const index = {};
          for (const row of rows) index[row.shopId] = indexEntry(row);
          copies.value = index;
          return index;
        })
        .catch((error) => {
          copiesLoaded = null;
          console.warn('[collector/prewarm] the preview copies could not be read', error);
        });
    }
    return copiesLoaded;
  }

  /** No signal, an offline preview on screen, or a live one: no recording now. */
  function mayRecord() {
    if (isReplayOn() || useAuthStore().isSupplierPreview) return false;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;
    return collectorNetwork().isOnline.value !== false;
  }

  async function removeCopies(shopIds) {
    for (const shopId of shopIds) {
      await previewStore().remove(shopId);
      const { [shopId]: _gone, ...rest } = copies.value;
      copies.value = rest;
    }
  }

  /**
   * The photos of the copies (L-N): after the copies, ONE pass for every shop,
   * on any connection, up to 600 pictures or 100 MB — whichever comes first —
   * in the owner's order (`selectPreviewPhotoUrls`); four at a time, skipping
   * what the cache holds and counting a picture only once it is IN the cache
   * (`runPrewarm`). Runs once per page load and again after any new copy, or
   * when the last one was cut short by the signal; each row keeps its share as
   * `photoUrls`.
   */
  async function runPreviewPhotos(day) {
    if (!previewPhotosDue || !mayRecord()) return;
    if (typeof caches === 'undefined' || !(await serviceWorkerControlsPage())) return;
    previewPhotosDue = false;
    const route = (day?.shops ?? []).map((shop) => String(shop?.supplierId));
    const rank = (row) => (route.includes(String(row.shopId)) ? route.indexOf(String(row.shopId)) : route.length);
    const rows = (await previewStore().list()).filter((row) => isUsableCopy(row)).sort((a, b) => rank(a) - rank(b));
    const { urls, byShop } = selectPreviewPhotoUrls(rows);
    let bytes = 0;
    // A pass that broke half-way is owed again (pwa-workbox-expert review).
    previewPhotosDue = true;
    await runPrewarm(urls, {
      has: inCache,
      load: (url) =>
        fetchPhoto(url, {
          onBytes: (n) => {
            bytes += n;
          },
        }),
      shouldStop: () => bytes >= PREVIEW_PHOTO_MAX_BYTES || !mayRecord(),
    });
    // Cut short by the signal (not by the budget): the signal's return carries on.
    previewPhotosDue = bytes < PREVIEW_PHOTO_MAX_BYTES && !mayRecord();
    for (const row of rows) {
      const share = byShop[String(row.shopId)] ?? [];
      if (share.join('\n') === (row.photoUrls ?? []).join('\n')) continue;
      const latest = await previewStore().get(row.shopId);
      if (latest) await previewStore().put({ ...latest, photoUrls: share });
    }
  }

  /**
   * «عروض المحلات»: save one shop's copy now, as picked (L-H, L-M) — needs
   * signal and no preview on screen; the oldest pick beyond 10 drops at once.
   * Rejects when it could not be saved (the previous copy stays).
   */
  async function savePicked(shopId, name = null) {
    if (!mayRecord()) throw new Error('[collector/prewarm] no signal to save a preview');
    await loadCopies();
    const row = await recordShop(shopId, { source: 'picked', name, pickedAt: new Date().toISOString() });
    copies.value = { ...copies.value, [row.shopId]: indexEntry(row) };
    await removeCopies(planPreviewPass(lastDay, Object.values(copies.value)).remove);
    previewPhotosDue = true;
    refreshPreviewCopies(lastDay); // its photos (and nothing else: every copy is fresh)
    return row;
  }

  /**
   * The preview pass (L-H). Never rejects, never blocks; a pass asked for while
   * one runs runs once more after it, with the newest day.
   * @param {object|null} day - the document the pictures were downloaded for (null: no day)
   */
  async function refreshPreviewCopies(day) {
    if (previewRunning) {
      previewPending = day;
      return;
    }
    previewRunning = true;
    try {
      await loadCopies();
      const plan = planPreviewPass(day, Object.values(copies.value));
      await removeCopies(plan.remove);
      for (const target of plan.record) {
        if (!mayRecord()) break;
        try {
          const row = await recordShop(target.shopId, { source: target.source, name: target.name });
          copies.value = { ...copies.value, [row.shopId]: indexEntry(row) };
          previewPhotosDue = true;
        } catch (error) {
          console.warn(`[collector/prewarm] the preview of shop ${target.shopId} was not saved`, error?.message ?? error);
          // No answer at all: the signal is gone — the next run carries on.
          if (error?.isAxiosError && error.response == null) break;
        }
      }
      await runPreviewPhotos(day);
    } catch (error) {
      console.warn('[collector/prewarm] the preview pass failed', error);
    } finally {
      previewRunning = false;
      if (previewPending !== undefined) {
        const waiting = previewPending;
        previewPending = undefined;
        refreshPreviewCopies(waiting);
      }
    }
  }

  /**
   * Download the day's pictures. Never rejects, never blocks.
   * @param {object|null} day - the document as the server sent it, or — with more than one open plan on the phone —
   *   the union of them (collector/prewarm.js `unionPrewarmDay`): «جاهز» then means every plan's pictures are in the cache
   * @param {{ force?: boolean }} [options] `force` ignores the pause after a failed run (the signal came back)
   */
  async function run(day, { force = false } = {}) {
    try {
      await runPhotos(day, { force });
    } finally {
      refreshPreviewCopies(day);
    }
  }

  async function runPhotos(day, { force = false } = {}) {
    if (!day) return;
    lastDay = day;
    if (running) {
      pendingDay = day;
      return;
    }
    const urls = selectPrewarmUrls(day);
    const signature = urls.join('\n');
    if (!force && phase.value === 'ready' && signature === readyFor) return;
    if (!force && Date.now() < pausedUntil) return;

    running = true;
    try {
      if (typeof caches === 'undefined' || !(await serviceWorkerControlsPage())) return;
      const result = await runPrewarm(urls, {
        has: inCache,
        load: (url) => fetchPhoto(url),
        shouldStop: () => typeof navigator !== 'undefined' && navigator.onLine === false,
        onProgress: (progress) => {
          done.value = progress.done;
          total.value = progress.total;
          // Nothing to fetch (everything cached): no flicker through "running".
          if (progress.done < progress.total) phase.value = 'running';
        },
      });
      if (result.done === result.total) {
        phase.value = 'ready';
        readyFor = signature;
        pausedUntil = 0;
      } else {
        phase.value = 'idle';
        readyFor = null;
        if (result.failed > 0) {
          console.warn(`[collector/prewarm] ${result.done} of ${result.total} pictures on the phone; ${result.failed} failed`);
          pausedUntil = Date.now() + FAILED_RUN_PAUSE_MS;
        }
      }
    } catch (error) {
      phase.value = 'idle';
      console.warn('[collector/prewarm] the morning download failed', error);
    } finally {
      running = false;
      const waiting = pendingDay;
      pendingDay = null;
      if (waiting) runPhotos(waiting);
    }
  }

  /** Forget the day (another account on this phone). */
  function reset() {
    phase.value = 'idle';
    done.value = 0;
    total.value = 0;
    lastDay = null;
    pendingDay = null;
    readyFor = null;
    pausedUntil = 0;
    copies.value = {};
    copiesLoaded = null;
  }

  // The signal is back → carry on where it stopped.
  watch(collectorNetwork().isOnline, (online) => {
    if (online && lastDay && !running && phase.value !== 'ready') run(lastDay, { force: true });
    else if (online) refreshPreviewCopies(lastDay);
  });

  // The marks show from the first paint of the day page, signal or not.
  loadCopies();

  return { phase, done, total, run, reset, copies, refreshPreviewCopies, savePicked };
});
